import { Swap } from '@drift-labs/icons';
import InputUi from 'src/components/primitives/Input/InputUi';
import { InputSize } from 'src/components/primitives/Input/types';
import { cj } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';

export interface ToggleInputOption<T extends string = string> {
	value: T;
	label: string;
}

type Props<T extends string = string> = WithClassName & {
	id: string;
	value: T;
	options: ToggleInputOption<T>[];
	onChange: (value: T) => void;
	disabled?: boolean;
	size?: InputSize;
} & (
		| {
				valuesOnly: true;
				label?: never;
		  }
		| {
				valuesOnly?: false;
				label: string;
		  }
	);

export default function ToggleInput<T extends string = string>({
	label,
	value,
	size = 'sm',
	options,
	onChange,
	disabled,
	valuesOnly,
}: Props<T>) {
	const currentOption = options.find((o) => o.value === value) ?? options[0];

	function handleToggle() {
		if (disabled) return;
		const currentIndex = options.findIndex((o) => o.value === value);
		const nextIndex =
			currentIndex >= 0 ? (currentIndex + 1) % options.length : 0;
		onChange(options[nextIndex].value);
	}

	return (
		<InputUi.Container
			className={cj(
				'justify-between',
				!disabled && 'has-active:border-purple-link'
			)}
			disabled={disabled}
			asChild
		>
			<button onClick={handleToggle} disabled={disabled}>
				<InputUi.Label
					size={size}
					className={cj(!disabled && valuesOnly && 'text-text-default')}
					disabled={disabled}
				>
					{valuesOnly ? (currentOption?.label ?? '') : label}
				</InputUi.Label>
				<InputUi.Suffix
					className="items-center gap-x-xs"
					size={size}
					disabled={disabled}
				>
					{!valuesOnly && (currentOption?.label ?? '')}
					<Swap size={16} />
				</InputUi.Suffix>
			</button>
		</InputUi.Container>
	);
}
